import {
  IconButton,
  Spacer,
  SvgCopy,
  SvgEdit,
  SvgSearch,
  Tooltip,
} from '@loomhq/lens';
import copy from 'copy-to-clipboard';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  toggleIsTranscriptSearchOpen,
  updateTranscriptPanelMode,
} from 'actions/shareVideo';
import { MOBILE_MAX_WIDTH } from 'constants/breakpoints';
import {
  TRANSCRIPTION_COPIED,
  TRANSCRIPTION_CORRECTION_OPENED,
  TRANSCRIPTION_SEARCH_OPENED,
} from 'constants/events';
import { TRANSCRIPT_PANEL_MODE_CORRECTION } from 'reducers/shareVideo';
import { selectUserIsOwner } from 'selectors/shareVideo';
import { selectTranscriptPhrases } from 'selectors/transcripts';
import { selectCurrentVideoId } from 'selectors/videoPlayer';
import * as analytics from 'utilities/analytics';
import { Transcript } from 'utilities/transcript/types';

import useMedia from 'hooks/useMedia';

import {
  ROLLOUT_IN_TRANSCRIPT_SEARCH,
  ROLLOUT_TRANSCRIPTION_CORRECTION,
} from '../../../../../../shared/constants/featureFlag';

import FeatureFlaggedComponent from 'components/feature-flag-wrapper';
import {
  COPY_TRANSCRIPT_TITLE,
  CORRECT_TRANSCRIPT_TITLE,
  SEARCH_TRANSCRIPT_TITLE,
} from 'components/share-video/transcript/constants';

const TranscriptHeaderIcons = (): JSX.Element => {
  const [copyTooltipContent, setCopyTooltipContent] = useState<string>(
    COPY_TRANSCRIPT_TITLE
  );
  const transcript: Transcript | null = useSelector(selectTranscriptPhrases);
  const showTranscriptButtons =
    transcript && transcript.phrases && transcript.phrases.length > 0;
  const currentUserIsOwner: boolean = useSelector(selectUserIsOwner);
  const videoId: string = useSelector(selectCurrentVideoId);
  const dispatch = useDispatch();

  const onCopyButtonClicked = () => {
    setCopyTooltipContent('Copied to clipboard!');
    // @ts-expect-error ignore due to enabling strict null checks
    const { phrases } = transcript;

    if (phrases.length < 1) {
      return;
    }

    let text = '';

    phrases.forEach(row => {
      text += row.value + '\n';
    });

    analytics.track(TRANSCRIPTION_COPIED, {
      currentUserIsOwner,
      video_id: videoId,
    });

    copy(text);
  };

  const onSearchButtonClicked = () => {
    dispatch(toggleIsTranscriptSearchOpen(true));

    analytics.track(TRANSCRIPTION_SEARCH_OPENED, {
      currentUserIsOwner,
      video_id: videoId,
    });
  };

  const onEditButtonClicked = () => {
    dispatch(updateTranscriptPanelMode(TRANSCRIPT_PANEL_MODE_CORRECTION));

    analytics.track(TRANSCRIPTION_CORRECTION_OPENED, {
      video_id: videoId,
    });
  };

  const iconSize = useMedia(
    [`(max-width: ${MOBILE_MAX_WIDTH}px)`],
    ['medium'],
    'small'
  );

  return (
    <>
      {showTranscriptButtons && (
        <>
          <FeatureFlaggedComponent
            flag={ROLLOUT_IN_TRANSCRIPT_SEARCH}
            activationValues={[true]}
          >
            <Spacer right={1}>
              <Tooltip
                content={SEARCH_TRANSCRIPT_TITLE}
                placement="bottomCenter"
              >
                <IconButton
                  altText={SEARCH_TRANSCRIPT_TITLE}
                  size={iconSize}
                  icon={<SvgSearch />}
                  onClick={onSearchButtonClicked}
                />
              </Tooltip>
            </Spacer>
          </FeatureFlaggedComponent>
          {currentUserIsOwner && (
            <FeatureFlaggedComponent
              flag={ROLLOUT_TRANSCRIPTION_CORRECTION}
              activationValues={['variant']}
            >
              <Spacer right={1}>
                <Tooltip
                  content={CORRECT_TRANSCRIPT_TITLE}
                  placement="bottomCenter"
                >
                  <IconButton
                    altText={CORRECT_TRANSCRIPT_TITLE}
                    size={iconSize}
                    icon={<SvgEdit />}
                    onClick={onEditButtonClicked}
                  />
                </Tooltip>
              </Spacer>
            </FeatureFlaggedComponent>
          )}
          <Spacer right={1}>
            <Tooltip content={copyTooltipContent} placement="bottomCenter">
              <IconButton
                altText={COPY_TRANSCRIPT_TITLE}
                size={iconSize}
                icon={<SvgCopy />}
                onClick={onCopyButtonClicked}
                onMouseEnter={() =>
                  setCopyTooltipContent(COPY_TRANSCRIPT_TITLE)
                }
              />
            </Tooltip>
          </Spacer>
        </>
      )}
    </>
  );
};

export default TranscriptHeaderIcons;
